import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  FunctionComponent,
  MouseEvent,
  Dispatch,
  SetStateAction,
} from 'react';

export interface ContextMenuProviderProps {
  readonly children: ReactNode;
}

export interface AnchorPoint {
  readonly x: number;
  readonly y: number;
}

export type MenuState<T extends object> = T & {
  readonly anchorPoint: AnchorPoint | undefined;
};

export interface ContextMenuActions<T extends object> {
  readonly handleMouseEvent: (event: MouseEvent, value: T) => void;
  readonly close: () => void;
}

export interface MenuProps<T extends object> {
  readonly actions: ContextMenuActions<T>;
  readonly state: MenuState<T>;
}

export type MenuComponent<T extends object> = FunctionComponent<MenuProps<T>>;

export interface CreateContextMenuParams<T extends object> {
  readonly name: string;
  readonly component: MenuComponent<T>;
  readonly initialValue: T;
}

export function createContextMenu<T extends object>(
  params: CreateContextMenuParams<T>,
) {
  const { name, initialValue, component: MenuComponent } = params;
  const initialState: MenuState<T> = {
    ...initialValue,
    anchorPoint: undefined,
  };

  function throwMissingProvider(): never {
    throw new Error(
      `Missing '${name}' context menu provider upwards in this tree`,
    );
  }

  const ActionsCtx = createContext<ContextMenuActions<T>>({
    handleMouseEvent: throwMissingProvider,
    close: throwMissingProvider,
  });
  ActionsCtx.displayName = `${name}ContextMenuActions`;

  function useContextMenuActions() {
    return useContext(ActionsCtx);
  }

  function ContextMenuProvider({ children }: ContextMenuProviderProps) {
    const [state, setState] = useState(initialState);

    const actions = useMemo(() => createActions(setState), []);

    return (
      <ActionsCtx.Provider value={actions}>
        {children}
        <MenuComponent actions={actions} state={state} />
      </ActionsCtx.Provider>
    );
  }

  return [useContextMenuActions, ContextMenuProvider] as const;
}

function createActions<T extends object>(
  setState: Dispatch<SetStateAction<MenuState<T>>>,
): ContextMenuActions<T> {
  return {
    handleMouseEvent(event, value) {
      event.preventDefault();
      setState({
        ...value,
        anchorPoint: { x: event.clientX, y: event.clientY },
      });
    },

    close() {
      setState((current) => ({ ...current, anchorPoint: undefined }));
    },
  };
}
